import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Plus, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import FinancingCaseDetail from "./FinancingCaseDetail";

const STATUS_TONE = {
  approved: "success", disbursed: "success", rejected: "destructive", withdrawn: "destructive",
};

function NewCaseDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const [f, setF] = useState({ case_name: "", linked_offer_id: "", financing_quantum_requested: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/green-finance/offers").then((r) => setOffers(r.data.offers)); }, [open]);
  const submit = async () => {
    if (!f.case_name || !f.linked_offer_id) return toast.error("Name and offer required");
    setBusy(true);
    try {
      const r = await api.post("/green-finance/cases", { ...f, financing_quantum_requested: Number(f.financing_quantum_requested || 0) });
      if (r.data.competing?.exceeds_need) toast.warning(`Combined requests ${formatSgd(r.data.competing.total_requested_sgd)} exceed this opportunity's ${formatSgd(r.data.competing.investment_required_sgd)} stated need`);
      setOpen(false); setF({ case_name: "", linked_offer_id: "", financing_quantum_requested: "" }); onCreated(); toast.success("Case created");
    }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)} data-testid="new-case-btn"><Plus className="h-4 w-4" /> New financing case</Button>
      <DialogContent className="max-w-lg" data-testid="new-case-dialog">
        <DialogHeader><DialogTitle className="font-display">New financing case</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><label className="text-[10px] text-muted-foreground">Case name</label><Input value={f.case_name} onChange={(e) => setF({ ...f, case_name: e.target.value })} data-testid="case-name-input" /></div>
          <div><label className="text-[10px] text-muted-foreground">Financing offer</label>
            <Select value={f.linked_offer_id} onValueChange={(v) => setF({ ...f, linked_offer_id: v })}><SelectTrigger className="h-9" data-testid="case-offer-select"><SelectValue placeholder="Select offer" /></SelectTrigger>
              <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.bank_name} · {o.offer_name}</SelectItem>)}</SelectContent></Select></div>
          <div><label className="text-[10px] text-muted-foreground">Requested quantum (S$)</label><Input type="number" value={f.financing_quantum_requested} onChange={(e) => setF({ ...f, financing_quantum_requested: e.target.value })} data-testid="case-quantum-input" /></div>
          <div className="flex justify-end"><Button variant="primary" size="sm" onClick={submit} disabled={busy} data-testid="case-create-submit">{busy ? "Creating…" : "Create"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FinancingCasesTab({ openCaseId, clearOpenCase }) {
  const [cases, setCases] = useState([]);
  const [dash, setDash] = useState(null);
  const [ref, setRef] = useState(null);
  const [sel, setSel] = useState(openCaseId || null);

  const load = () => {
    api.get("/green-finance/cases").then((r) => setCases(r.data.cases));
    api.get("/green-finance/dashboard").then((r) => setDash(r.data));
  };
  useEffect(() => { api.get("/green-finance/reference").then((r) => setRef(r.data)); load(); }, []);
  useEffect(() => { if (openCaseId) setSel(openCaseId); }, [openCaseId]);

  if (sel) return <FinancingCaseDetail caseId={sel} refData={ref} onBack={() => { setSel(null); clearOpenCase && clearOpenCase(); load(); }} />;

  return (
    <div className="space-y-4" data-testid="gf-cases-tab">
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div className="card-flat p-3" data-testid="cases-stat-total"><div className="label-eyebrow text-[10px]">Total cases</div><div className="font-mono text-xl font-bold">{dash.total_cases}</div></div>
          <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">In progress</div><div className="font-mono text-xl font-bold text-[#FFB800]">{dash.in_progress}</div></div>
          <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Secured</div><div className="font-mono text-xl font-bold text-[#00E65B]">{dash.secured}</div></div>
          <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Requested</div><div className="font-mono text-lg font-bold">{formatSgd(dash.requested_quantum_sgd)}</div></div>
          <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Approved</div><div className="font-mono text-lg font-bold">{formatSgd(dash.approved_quantum_sgd)}</div></div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="font-display text-lg font-semibold inline-flex items-center gap-2"><FolderKanban className="h-5 w-5 text-accent" /> Financing cases</div>
        <NewCaseDialog onCreated={load} />
      </div>
      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Case</TableHead><TableHead>Bank · offer</TableHead><TableHead className="text-right">Requested</TableHead>
            <TableHead>Owner</TableHead><TableHead>Checklist</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {cases.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => setSel(c.id)} data-testid={`case-row-${c.id}`}>
                <TableCell className="text-sm font-medium">{c.case_name}
                  {c.linked_initiative_name && (
                    <div className="text-[10px] text-accent mt-0.5" data-testid={`case-funds-${c.id}`}>
                      Funds: {c.linked_initiative_name}
                    </div>
                  )}
                  {c.competing && (
                    <div className="mt-0.5">
                      <Badge variant={c.competing.exceeds_need ? "warning" : "outline"} className="text-[9px]" data-testid={`competing-badge-${c.id}`}>
                        Competing option · {c.competing.count} open case{c.competing.count === 1 ? "" : "s"}
                        {c.competing.exceeds_need && c.competing.investment_required_sgd
                          ? ` · ${formatSgd(c.competing.total_requested_sgd)} requested vs ${formatSgd(c.competing.investment_required_sgd)} need`
                          : ""}
                      </Badge>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.bank_name} · {c.linked_offer_snapshot?.offer_name}</TableCell>
                <TableCell className="text-right font-mono text-xs">{formatSgd(c.financing_quantum_requested)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.case_owner}</TableCell>
                <TableCell className="text-xs font-mono">{c.checklist_summary?.percent}%</TableCell>
                <TableCell><Badge variant={STATUS_TONE[c.case_status] || "outline"} className="text-[9px]">{c.status_label}</Badge></TableCell>
              </TableRow>
            ))}
            {!cases.length && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">No financing cases yet. Create one here or from an initiative’s matches.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
